import {
	commissionRateCreate,
	commissionRateDestroy,
	commissionRateList,
	commissionRateUpdate,
	dealsClicks,
	dealsCommissions,
	dealsIntegrations,
	dealsLinkChecks,
	dealsOverview,
	dealsQuality,
	dealsRevenue,
	dealsStores,
	dealsSyncRuns,
	dealsUntapped,
	networkTransactionCreate,
	networkTransactionDestroy,
	networkTransactionImport,
	networkTransactionList,
	networkTransactionUpdate,
} from '@/services/shop/dealsDashboard.service'
import { retailerList } from '@/services/shop/retailers.service'

// One slot per screen of the dashboard. A slot is { data, meta, status, error }: status is init | pending | ok | error.
const LOADERS = {
	overview: dealsOverview,
	stores: dealsStores,
	integrations: dealsIntegrations,
	clicks: dealsClicks,
	commissions: dealsCommissions,
	untapped: dealsUntapped,
	revenue: dealsRevenue,
	syncRuns: dealsSyncRuns,
	linkChecks: dealsLinkChecks,
	quality: dealsQuality,
	transactions: networkTransactionList,
	rates: commissionRateList,
}

// What only a platform admin may read: a 403 from one of these hides the money tabs for good.
const MONEY_SLOTS = ['commissions', 'untapped', 'revenue', 'transactions', 'rates']

const emptySlot = () => ({ data: null, meta: null, status: 'init', error: null })

// The newest request of a slot wins: a slow answer to an older filter must not overwrite the one the screen is showing.
const latest = {}

export default {
	namespaced: true,
	state: () => ({
		slots: Object.keys(LOADERS).reduce((all, key) => ({ ...all, [key]: emptySlot() }), {}),
		canSeeMoney: false,
		moneyForbidden: false,
		retailers: [],
	}),
	getters: {
		slot: s => key => s.slots[key],
		canSeeMoney: s => s.canSeeMoney && !s.moneyForbidden,
		moneyForbidden: s => s.moneyForbidden,
		retailers: s => s.retailers,
	},
	mutations: {
		SET_PENDING(state, key) {
			state.slots[key].status = 'pending'
			state.slots[key].error = null
		},
		SET_LOADED(state, { key, data, meta }) {
			state.slots[key] = { data, meta, status: 'ok', error: null }
		},
		SET_FAILED(state, { key, error }) {
			// What was on screen stays: a failed refresh does not blank a table that already had rows.
			state.slots[key].status = 'error'
			state.slots[key].error = error
		},
		SET_CAN_SEE_MONEY(state, value) {
			state.canSeeMoney = value === true
		},
		SET_MONEY_FORBIDDEN(state) {
			state.moneyForbidden = true
		},
		SET_RETAILERS(state, value) {
			state.retailers = value
		},
	},
	actions: {
		async load({ commit }, { key, params = {} }) {
			const call = LOADERS[key]
			const ticket = (latest[key] = (latest[key] || 0) + 1)
			commit('SET_PENDING', key)
			try {
				const { data } = await call(params)
				if (ticket !== latest[key]) return
				commit('SET_LOADED', { key, data: data?.data ?? null, meta: data?.meta ?? {} })
				if (key === 'overview') commit('SET_CAN_SEE_MONEY', data?.meta?.can_see_money)
			} catch (e) {
				if (ticket !== latest[key]) return
				const status = e?.response?.status || 0
				if (status === 403 && MONEY_SLOTS.includes(key)) commit('SET_MONEY_FORBIDDEN')
				commit('SET_FAILED', { key, error: { status, message: e?.response?.data?.message || null } })
			}
		},

		/** The stores of the registry, for the store pickers. A failure leaves the pickers empty: the filters still work. */
		async loadRetailers({ commit }) {
			try {
				const { data } = await retailerList({ per_page: 'all' })
				commit(
					'SET_RETAILERS',
					(data?.data || []).map(r => ({ id: r.id, slug: r.slug, name: r.name })),
				)
			} catch (e) {
				commit('SET_RETAILERS', [])
			}
		},

		// The id travels in the URL, not in the body.
		saveRate(context, { id, ...body }) {
			return id ? commissionRateUpdate(id, body) : commissionRateCreate(body)
		},
		removeRate(context, id) {
			return commissionRateDestroy(id)
		},
		saveTransaction(context, { id, ...body }) {
			return id ? networkTransactionUpdate(id, body) : networkTransactionCreate(body)
		},
		removeTransaction(context, id) {
			return networkTransactionDestroy(id)
		},
		importTransactions(context, file) {
			return networkTransactionImport(file)
		},
	},
}
